import { useState, useCallback, useMemo } from "react";
import { getItems } from "../../api/items";
import { useUpdateRecords } from "../../api/useUpdateRecords";

type NotificationItem = {
    id: string;
    is_read: boolean | null;
    type: string;
    navigation_path: string;
};

const createFilterForTask = (taskId: number | string) => ({
    _and: [
        { _or: [{ is_read: { _eq: false } }, { is_read: { _null: true } }] },
        { user_to_be_notified: { _eq: "$CURRENT_USER" } },
        { _or: [{ type: { _eq: "tasks" } }, { type: { _eq: "task_comments" } }] },
        { navigation_path: { _contains: `/tasks/${taskId}/` } },
    ],
});

export const useTaskNotificationsRead = (taskId: number | string) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [filteredNotifications, setFilteredNotifications] = useState<Array<NotificationItem>>([]);

    const taskFilter = useMemo(() => createFilterForTask(taskId), [taskId]);

    const updateRecords = useUpdateRecords({ queryInvalidatePrefix: "notifications" });

    const checkAndPrompt = useCallback(async () => {
        const refreshedData = await getItems("notifications", "id,is_read,type,navigation_path", { filter: taskFilter });
        const notifications = refreshedData.items || [];
        setFilteredNotifications(notifications);
        const hasNotifications = notifications.length > 0;

        if (hasNotifications) {
            setIsDialogOpen(true);
        }

        return hasNotifications;
    }, [taskFilter]);

    const handleConfirm = useCallback(async () => {
        const notificationIds = filteredNotifications.map((n: NotificationItem) => n.id);
        const updateData = notificationIds.map((id: string) => ({ id, is_read: true }));

        await updateRecords.mutateAsync({
            entity: "notifications",
            data: updateData,
        });

        setFilteredNotifications([]);
        setIsDialogOpen(false);
    }, [filteredNotifications, updateRecords]);

    const handleCancel = useCallback(() => {
        setFilteredNotifications([]);
        setIsDialogOpen(false);
    }, []);

    return {
        dialogProps: {
            open: isDialogOpen,
            notificationCount: filteredNotifications.length,
            onConfirm: handleConfirm,
            onCancel: handleCancel,
        },
        checkAndPrompt,
    };
};

